import { defaultFetchHeaders, fetchJson } from "../../utils";

export type HealthStatus = "healthy" | "degraded" | "down" | "unknown";
export type AdministrationHealth = {
  generatedAt: string;
  overallStatus: HealthStatus;
  components: Array<{
    component: string; label: string; group: "edge" | "data" | "workers" | "host"; status: HealthStatus; summary: string;
    lastHeartbeatAt: string | null; nextChangeAt?: string | null; latencyMs: number | null; queueDepth: number | null; applicable: boolean;
  }>;
  services?: Array<{
    key: string; label: string; unit: string; status: HealthStatus; supervisorState: string;
    lastHeartbeatAt: string | null; latencyMs: number | null;
  }>;
  queues?: Array<{
    key: string; label: string; depth: number; worker: string; workerLabel: string; workerStatus: HealthStatus;
  }>;
};
export type AuditSeverity = "info" | "warning" | "error" | "critical";
export type AdministrationAuditEvent = {
  id: string; createdAt: string; severity: AuditSeverity; category: string;
  component: string; action: string; summary: string;
  details: Record<string, unknown>; correlationId: string;
  actor: { id: string; name: string; email: string } | null;
};
export type AdministrationAuditPage = { items: AdministrationAuditEvent[]; nextCursor: string | null; total: number; severityTotals: Record<AuditSeverity, number>; limit: number; offset: number };
export const mergeAdministrationAuditPages = (current: AdministrationAuditPage, next: AdministrationAuditPage): AdministrationAuditPage => ({
  ...next,
  items: [...current.items, ...next.items.filter((item) => !current.items.some((existing) => existing.id === item.id))],
  nextCursor: next.nextCursor,
});

const backend = process.env.REACT_APP_BACKEND_URL || "";
const get = async <T>(path: string, message: string) => fetchJson<T>(
  `${backend}${path}`,
  { method: "GET", credentials: "include", cache: "no-store", headers: await defaultFetchHeaders() },
  message,
);

export type AdministrationQrInvite = { inviteUrl?: string | null; expiresAt?: string | null };
export const fetchAdministrationOrganizationQrInvite = async (
  organizationId: string,
  role: string,
  fresh = false,
) => fetchJson<AdministrationQrInvite>(
  `${backend}/administration/organizations/${encodeURIComponent(organizationId)}/people/invitations/qr?fresh=${fresh ? "true" : "false"}`,
  {
    method: "POST",
    credentials: "include",
    headers: { ...(await defaultFetchHeaders()), "Content-Type": "application/json" },
    body: JSON.stringify({ role }),
  },
  "Failed to load organization QR invite",
);

export const fetchAdministrationHealth = () =>
  get<AdministrationHealth>("/administration/health", "Failed to load backend health");

export const requestAdministrationHealthChecks = async () => fetchJson<{ acceptedAt: string }>(
  `${backend}/administration/health/checks`,
  { method: "POST", credentials: "include", headers: await defaultFetchHeaders() },
  "Failed to request backend health checks",
);

export const fetchAdministrationAudit = (filters: { q?: string; severity?: string; category?: string; cursor?: string; dateFrom?: string; dateTo?: string; limit?: number; offset?: number; sort?: string; direction?: string } = {}) => {
  const { dateFrom, dateTo, ...rest } = filters;
  const params = new URLSearchParams({ limit: String(filters.limit || 25), offset: String(filters.offset || 0) });
  Object.entries(rest).forEach(([key, value]) => { if (value && key !== "limit" && key !== "offset") params.set(key, String(value)); });
  if (dateFrom) params.set("date_from", dateFrom);
  if (dateTo) params.set("date_to", dateTo);
  return get<AdministrationAuditPage>(`/administration/audit?${params}`, "Failed to load audit log");
};

export type AdministrationOrganizationActivityQuery = {
  q?: string;
  domain: "all" | "billing" | "people" | "network";
  severity: string;
};

export const fetchAdministrationOrganizationActivity = (
  organizationId: string,
  filters: AdministrationOrganizationActivityQuery & { cursor?: string },
) => {
  const params = new URLSearchParams({ limit: "50", domain: filters.domain });
  if (filters.q) params.set("q", filters.q);
  if (filters.severity) params.set("severity", filters.severity);
  if (filters.cursor) params.set("cursor", filters.cursor);
  return get<AdministrationAuditPage>(
    `/administration/organizations/${encodeURIComponent(organizationId)}/activity?${params}`,
    "Failed to load organization activity",
  );
};
